import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useFullPageScroll } from "../../Controller/Utils/useFullPageScroll";
import { SCROLL_SPEED, EASE_CUSTOM } from "../../Styles/theme";
import Main from "./Main/Main";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";

const Container = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;

  @media (max-width: 1200px) {
    height: auto;
    overflow: visible;
  }
`;

export const ContentWrapper = styled.div`
  height: 100%;
  transition: transform ${SCROLL_SPEED}ms ${EASE_CUSTOM};

  transform: translateY(
    ${(props) => {
      const baseTranslate = props.page * 100;
      if (props.showFooter) return `calc(-${baseTranslate}% - 300px)`;
      return `-${baseTranslate}%`;
    }}
  );

  @media (max-width: 1200px) {
    transition: none;
    transform: none !important;
    height: auto;
    display: flex;
    flex-direction: column;
  }
`;

const SectionContainer = styled.section`
  width: 100%;
  height: 100vh;
  flex-shrink: 0;

  @media (max-width: 1200px) {
    height: auto;
    min-height: 100vh;
  }

  &:last-child {
    @media (max-width: 1200px) {
      height: auto;
      min-height: auto;
    }
  }
`;

const SectionInner = styled.div`
  width: 100%;
  height: 100vh;

  @media (max-width: 1200px) {
    height: auto;
    min-height: 100vh;
  }
`;

const Indicator = styled.div`
  position: fixed;
  right: 25px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 30px;
  @media (max-width: 1200px) {
    display: none;
  }
`;

const DotWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const Dot = styled.div`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.active ? "transparent" : "rgba(255, 255, 255, 0.3)"};
  cursor: pointer;

  ${(props) =>
    props.active &&
    `
    background-image: url('/images/Nav/nav_icon_new.png');
    width: 15px; 
    height: 15px;
    border-radius: 0; 
    transform: scale(1.6);
    background-repeat: no-repeat;
    background-size: cover;
  `}
`;

const Home = () => {
  const SECTIONS_DATA = [{ id: 0, label: "Home", component: <Main /> }];

  const totalSections = SECTIONS_DATA.length;

  const { currentPage, setCurrentPage, isDesktop } =
    useFullPageScroll(totalSections);
  const [showFooter, setShowFooter] = useState(false);
  const isLocked = useRef(false);

  useEffect(() => {
    if (isDesktop) {
      isLocked.current = true;
      const timer = setTimeout(() => {
        isLocked.current = false;
      }, SCROLL_SPEED + 150);

      return () => clearTimeout(timer);
    }
  }, [currentPage, showFooter, isDesktop]);

  const handleWheel = useCallback(
    (e) => {
      if (!isDesktop || isLocked.current) return;

      const isLastPage = currentPage === totalSections - 1;

      if (isLastPage) {
        if (e.deltaY > 0 && !showFooter) {
          e.preventDefault();
          setShowFooter(true);
        } else if (e.deltaY < 0 && showFooter) {
          e.preventDefault();
          setShowFooter(false);
        }
      }
    },
    [currentPage, showFooter, totalSections, isDesktop],
  );

  useEffect(() => {
    if (!isDesktop) return;

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [handleWheel, isDesktop]);

  return (
    <>
      <Header />
      <Container>
        <ContentWrapper page={currentPage} showFooter={showFooter}>
          {SECTIONS_DATA.map((item) => (
            <SectionContainer key={item.id}>
              <SectionInner>{item.component}</SectionInner>
              {item.id === totalSections - 1 && <Footer />}
            </SectionContainer>
          ))}
        </ContentWrapper>
      </Container>
    </>
  );
};

export default Home;
